<cb-dialog
    class="lot-migrator-execution-preview"
    dialogHeading="Migrate Lots Progress Preview"
    [canClose]="canCloseDialog"
>
    <ng-template #statusIndicator let-status="status">
        <span class="execution-status" [ngClass]="getStatusClass(status)">
            @if (status === 'running') {
                <mat-spinner [diameter]="18" [strokeWidth]="3"></mat-spinner>
            } @else {
                <mat-icon>{{ getStatusIcon(status) }}</mat-icon>
            }
            <span>{{ getStatusLabel(status) }}</span>
        </span>
    </ng-template>

    <div class="execution-preview">
        <div class="execution-preview__banner">
            <div>
                <h3>Execution progress</h3>
                <p>{{ headerMessage }}</p>
            </div>
        </div>

        <section class="execution-section">
            <div class="execution-section__header">
                <div>
                    <h3>1. Pre-Migration Checks</h3>
                    <p>Re-check the selected lots before any migration work starts.</p>
                </div>
                <ng-container
                    *ngTemplateOutlet="statusIndicator; context: { status: preMigrationStepStatus }"
                ></ng-container>
            </div>

            <div class="execution-list">
                @for (row of preMigrationRows; track row.id) {
                    <div class="execution-row">
                        <div class="execution-row__content">
                            <strong>Check Lot {{ row.lotNumber }}</strong>
                            @if (row.childLots.length > 0) {
                                <div class="execution-row__children">
                                    @for (childLot of row.childLots; track childLot.id) {
                                        <span class="execution-row__child">
                                            Child Lot {{ childLot.lotNumber }}
                                        </span>
                                    }
                                </div>
                            }
                            @if (row.errorMessage) {
                                <span class="execution-row__error">{{ row.errorMessage }}</span>
                            }
                        </div>
                        <div class="execution-row__actions">
                            <ng-container
                                *ngTemplateOutlet="
                                    statusIndicator;
                                    context: { status: row.status }
                                "
                            ></ng-container>
                        </div>
                    </div>
                }
            </div>

            @if (hasFailedPreMigrationChecks) {
                <div class="execution-inline-actions">
                    <button
                        mat-stroked-button
                        color="primary"
                        (click)="retryPreMigrationChecks()"
                    >
                        Retry Checks
                    </button>
                </div>
            }
        </section>

        <section class="execution-section">
            <div class="execution-section__header">
                <div>
                    <h3>2. Cancel existing lots</h3>
                    <p>If a lot cancellation fails, please check the lot and retry when the issue has been corrected.</p>
                </div>
                <ng-container
                    *ngTemplateOutlet="statusIndicator; context: { status: cancelStepStatus }"
                ></ng-container>
            </div>

            <div class="execution-list">
                @for (row of cancelRows; track row.id) {
                    <div class="execution-row">
                        <div class="execution-row__content">
                            <strong>Cancel Lot {{ row.lotNumber }}</strong>
                            @if (row.errorMessage) {
                                <span class="execution-row__error">{{ row.errorMessage }}</span>
                            }
                        </div>
                        <div class="execution-row__actions">
                            <ng-container
                                *ngTemplateOutlet="
                                    statusIndicator;
                                    context: { status: row.status }
                                "
                            ></ng-container>
                            @if (row.status === 'error') {
                                <button
                                    mat-stroked-button
                                    color="primary"
                                    (click)="retryCancellation(row)"
                                >
                                    Retry
                                </button>
                            }
                        </div>
                    </div>
                }
            </div>

            @if (hasFailedCancellations) {
                <p class="execution-note">
                    Retry failed cancellations before the import step can continue.
                </p>
            }
        </section>

        @if (showImportStep) {
            <section class="execution-section">
                <div class="execution-section__header">
                    <div>
                        <h3>3. Create new lots</h3>
                        <p>
                            If the import fails, please download the CSV, correct the issues and then re-import using the
                            lot importer
                        </p>
                    </div>
                    <ng-container
                        *ngTemplateOutlet="statusIndicator; context: { status: importStep.status }"
                    ></ng-container>
                </div>

                <div class="execution-callout">
                    <div class="execution-callout__header">
                        <strong>Import Lots</strong>
                    </div>
                    <div class="execution-chip-list">
                        @for (lot of data.newLotSummaries; track lot.lotNumber) {
                            <span class="execution-chip">
                                Lot {{ lot.lotNumber }}
                                <small>{{ lot.lotType }}</small>
                            </span>
                        }
                    </div>

                    @if (importStep.errorMessage) {
                        <div class="execution-error-banner">
                            <mat-icon>warning</mat-icon>
                            <span>{{ importStep.errorMessage }}</span>
                        </div>
                    }

                    @if (importStep.status === 'error') {
                        <div class="execution-inline-actions">
                            <button mat-stroked-button color="primary" (click)="downloadCsv()">
                                Download CSV
                            </button>
                            <button
                                mat-flat-button
                                color="primary"
                                (click)="openImportRetryDialog()"
                            >
                                Upload CSV
                            </button>
                        </div>
                    }
                </div>
            </section>
        }

        @if (showDocumentStep) {
            <section class="execution-section">
                <div class="execution-section__header">
                    <div>
                        <h3>4. Migrate lot documents</h3>
                        <p>If a document fails to migrate, please download it now for later manual upload</p>
                    </div>
                    <ng-container
                        *ngTemplateOutlet="
                            statusIndicator;
                            context: { status: documentStepStatus }
                        "
                    ></ng-container>
                </div>

                @if (documentRows.length < 1) {
                    <p class="execution-note">
                        No documents were selected, so this step is skipped.
                    </p>
                } @else {
                    <div class="execution-list">
                        @for (row of documentRows; track row.id) {
                            <div class="execution-row">
                                <div class="execution-row__content">
                                    <strong>{{ row.documentName }}</strong>
                                    <span class="execution-row__meta">
                                        Lot {{ row.sourceLotNumber || '-' }} to Lot
                                        {{ row.destinationLotNumber || '-' }}
                                    </span>
                                    @if (row.errorMessage) {
                                        <span class="execution-row__error">
                                            {{ row.errorMessage }}
                                        </span>
                                    }
                                </div>
                                <div class="execution-row__actions">
                                    <ng-container
                                        *ngTemplateOutlet="
                                            statusIndicator;
                                            context: { status: row.status }
                                        "
                                    ></ng-container>
                                    @if (row.status === 'error') {
                                        <button
                                            mat-stroked-button
                                            color="primary"
                                            (click)="downloadDocument(row)"
                                        >
                                            Download
                                        </button>
                                    }
                                </div>
                            </div>
                        }
                    </div>
                }
            </section>
        }
    </div>

    <div actions>
        <button mat-raised-button [disabled]="!canCloseDialog" (click)="close()">Close</button>
        @if (canNotifyAndComplete) {
            <button mat-flat-button color="primary" (click)="notifyAndComplete()">
                Complete
            </button>
        }
    </div>
</cb-dialog>
